import React from 'react';
import { Heart, GraduationCap, Sparkles } from 'lucide-react';

const galleryPhotos = [
  {
    id: 'g1',
    src: 'https://plus.unsplash.com/premium_photo-1726876849346-14a560078474?fm=jpg&q=85&w=1200&auto=format&fit=crop',
    alt: 'Niños pequeños felices en la escuela',
    caption: 'Little Speakers',
    tag: '',
    accent: '#E8B84A',
    span: 'md:col-span-2',
  },
  {
    id: 'g2',
    src: 'https://images.unsplash.com/photo-1588072432836-e10032774350?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    alt: 'Alumno pequeño escribiendo en clase',
    caption: 'Aprender jugando',
    tag: 'Kids',
    accent: '#5B84A6',
    span: '',
  },
  {
    id: 'g3',
    src: 'https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    alt: 'Alumno recibiendo diploma Cambridge',
    caption: 'Diploma Cambridge',
    tag: 'Logros',
    accent: '#9B2C3B',
    span: '',
  },
  {
    id: 'g4',
    src: 'https://images.unsplash.com/photo-1544717305-2782549b5136?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    alt: 'Jóvenes estudiando juntos',
    caption: 'Young Explorers',
    tag: 'Teens',
    accent: '#5B84A6',
    span: '',
  },
  {
    id: 'g5',
    src: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
    alt: 'Celebración de graduación',
    caption: 'Celebrando logros',
    tag: 'Cambridge',
    accent: '#9B2C3B',
    span: '',
  },
  {
    id: 'g6',
    src: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85',
    alt: 'Alumnos y docente en clase',
    caption: 'Comunidad Pinar',
    tag: 'Grupos',
    accent: '#E8B84A',
    span: 'md:col-span-2',
  },
];

const stats = [
  { icon: Heart, value: '+300', label: 'alumnos', sub: 'aprenden con nosotros' },
  { icon: GraduationCap, value: '96%', label: 'aprobados', sub: 'en exámenes Cambridge' },
  { icon: Sparkles, value: '17', label: 'años', sub: 'de trayectoria' },
];

const Gallery = () => {
  return (
    <section
      id="galeria"
      className="bg-[#FAF6EC] py-20 md:py-24 relative overflow-hidden"
    >
      {/* Decorative dots */}
      <div className="absolute top-16 right-10 grid grid-cols-6 gap-1.5 opacity-25">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-[#1E3A5F]" />
        ))}
      </div>
      <div className="absolute bottom-16 left-10 grid grid-cols-5 gap-1.5 opacity-20">
        {Array.from({ length: 20 }).map((_, i) => (
          <span key={i} className="w-1 h-1 rounded-full bg-[#B08D3C]" />
        ))}
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-2 gap-8 items-end mb-12">
          <div>
            <span className="inline-block text-[11px] font-semibold tracking-widest text-[#9B2C3B] px-3 py-1 border border-[#9B2C3B]/40 rounded-full">
              GALERÍA
            </span>
            <h2 className="mt-5 font-serif text-4xl md:text-5xl leading-[1.05] font-bold text-[#1E3A5F]">
              Momentos{' '}
              <span className="text-[#E8B84A] italic">Pinar.</span>
            </h2>
          </div>
          <p className="text-[15px] text-[#1E3A5F]/70 leading-relaxed max-w-md">
            Un vistazo a lo que se vive en la academia: clases, amigos,
            certificaciones y celebraciones. Esta es nuestra comunidad.
          </p>
        </div>

        {/* Photo mosaic grid: 4 cols on md+, P1 & P6 span 2 cols */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[240px] gap-4">
          {galleryPhotos.map((p) => (
            <figure
              key={p.id}
              className={`group relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 ${p.span}`}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D1F32]/85 via-[#0D1F32]/10 to-transparent" />

              {p.tag && (
                <span
                  className="absolute top-3 left-3 text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full backdrop-blur"
                  style={{
                    backgroundColor: `${p.accent}E6`,
                    color: p.accent === '#E8B84A' ? '#1E3A5F' : '#FAF6EC',
                  }}
                >
                  {p.tag}
                </span>
              )}

              <figcaption className="absolute bottom-3 left-3 right-3 font-serif text-white text-[15px] md:text-[17px] font-semibold leading-tight drop-shadow-md">
                {p.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Stats bar */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-4 bg-white rounded-2xl px-5 py-4 border border-[#1E3A5F]/8 shadow-sm"
            >
              <span className="h-11 w-11 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
                <s.icon className="h-5 w-5 text-[#E8B84A]" />
              </span>
              <div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-serif text-2xl font-bold text-[#9B2C3B] leading-none">
                    {s.value}
                  </span>
                  <span className="text-[13px] font-semibold text-[#1E3A5F]">
                    {s.label}
                  </span>
                </div>
                <div className="text-[12px] text-[#1E3A5F]/60 mt-1">
                  {s.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
